import { Doc } from '@dadohr/shared/domainTypes/document';
import { IExperienceTemplate, ITaskDenormalized, RequirementType, SubTaskDenormalized, SubTaskStatus } from '@dadohr/shared/domainTypes/experience';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@material-ui/core';
import { AlertCircle, Redo } from 'lucide-react';
import React, { useState } from 'react';
import { ButtonWithoutPropagation } from '../../../../../components/ButtonWithoutPropagation';
import { ButtonWithPromise } from '../../../../../components/ButtonWithPromise';
import { FlexContainer } from '../../../../../layout/Flex';
import { getTriggeredItemsForTask } from '../../../../../services/experiences/template';
import { useProductAnalytics } from '../../../../../services/productAnalytics';
import { TriggeredItemsList } from '../../../components/TriggeredItemsList';

interface SkipLastSubTaskButtonProps {
  subTask: SubTaskDenormalized;
  task: ITaskDenormalized;
  template: Doc<IExperienceTemplate>;
  onSkipNoTrigger: (subTaskId: string) => void;
  onSkipSubTask: (subTaskId: string) => Promise<void>;
  enqueueSnackbarSuccess: (message: string) => void;
  enqueueSnackbarError: (message: string) => void;
  t: (key: string) => string;
}

export const SkipLastSubTaskButton: React.FC<SkipLastSubTaskButtonProps> = ({
  subTask,
  task,
  template,
  onSkipNoTrigger,
  onSkipSubTask,
  enqueueSnackbarSuccess,
  enqueueSnackbarError,
  t
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { trackEvent } = useProductAnalytics();

  // Mixpanel tracking
  const trackTrigger = () => {
    trackEvent({
      eventType: PAEventType.EXPERIENCE_INSTANCE,
      name: 'Sub-Task Skipped',
      Explanation: 'Admin skips a sub-task in an instance',
      Subdomain: 'app.dadohr',
      'Experience ID': template.id,
      'Experience Name': template.data.adminTitle || template.data.title,
      'Timeline Item ID': task.templateId.replace('-triggered', ''),
      'Timeline Item Type': 'task',
      'Timeline Item Title': task.title,
      'Timeline Item Status': task.status,
      'Instance ID': task.experienceId,
      'Skipping Completes Task': true
    })
  }

  // Check if this is the last open subtask
  const isLastOpenSubTask = () => {
    const openSubTasks = task.subTasks.filter(s => s.status === SubTaskStatus.OPEN && s.requirementType === RequirementType.MANDATORY);
    return openSubTasks.length === 1 && openSubTasks[0].id === subTask.id;
  };

  const handleConfirmedSkip = async () => {
    setIsLoading(true);
    try {
      await onSkipSubTask(subTask.id);
      enqueueSnackbarSuccess('Subtask skipped successfully. New tasks may have been triggered.');

      try {
        trackTrigger();
      } catch (error: any) {
        console.error('Mixpanel tracking failed:', error);
      }
      
      setShowConfirmDialog(false);
    } catch (error: any) {
      console.error('Skip subtask failed:', error);
      enqueueSnackbarError(error?.message ? error.message : 'Failed to skip subtask');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onSkipNoTrigger(subTask.id);
    setShowConfirmDialog(false);
  }

  if (subTask.status !== SubTaskStatus.OPEN || !isLastOpenSubTask()) {
    return null;
  }

  const triggeredItems = getTriggeredItemsForTask(template, task.templateId);

  return (
    <>
      <ButtonWithoutPropagation
        variant="outlined"
        size="small"
        onClick={() => setShowConfirmDialog(true)}
        startIcon={<Redo size={16} />}
      >
        {t('Skip')}
      </ButtonWithoutPropagation>
      
      <Dialog
        open={showConfirmDialog}
        onClose={() => handleCancel()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <FlexContainer justifyContent="start" spacing={1}>
            <AlertCircle />
            {t('Trigger the follow-on effects of this task?')}
          </FlexContainer>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" component="p" style={{ paddingBottom: '16px' }}>
            {t('You\'ve skipped the final required sub-task in this task. Do you want to mark the task complete, and trigger the following timeline items')}:
          </Typography>
          <TriggeredItemsList
            triggeredItems={triggeredItems}
            tplContext={template.data}
            onGoToOtherItem={() => {}}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => handleCancel()} 
            color="default"
            disabled={isLoading}
          >
            {t('Don\'t trigger')}
          </Button>
          <ButtonWithPromise
            onClick={handleConfirmedSkip}
            pending={t('Triggering...')}
            color="primary"
            variant="contained"
            startIcon={<Redo size={16} />}
          >
            {t('Trigger next items')}
          </ButtonWithPromise>
        </DialogActions>
      </Dialog>
    </>
  );
};